﻿import React, { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Box, Alert, Tabs, Tab } from "@mui/material";

import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";

import OverviewTab from "./Tabs/OverviewTab";
import BaseArticlesTab from "./Tabs/BaseArticlesTab";
import OttoTab from "./Tabs/OttoTab";
import KauflandTab from "./Tabs/KauflandTab";
import ShopifyTab from "./Tabs/ShopifyTab";
import FbmTab from "./Tabs/FbmTab";
import FbaTab from "./Tabs/FbaTab";
import TaskStatusWidget from "./TaskStatusWidget";

const PLATFORM_TAB_INDEX = { otto: 2, kaufland: 3, shopify: 4, fbm: 5, fba: 6 };

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const MultiRepricerPage = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // { platform: "kaufland"|"otto"|"shopify", sku: string } — cleared after consumption
  const [platformNav, setPlatformNav] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleNavigateToPlatform = (platform, sku) => {
    const idx = PLATFORM_TAB_INDEX[platform];
    if (!idx) return;
    setPlatformNav({ platform, sku });
    setTabValue(idx);
  };

  const handleNavigateToBase = (sku) => {
    setPlatformNav({ platform: "base", sku });
    setTabValue(1);
  };

  useEffect(() => {
    const stateNav = location.state;
    if (stateNav?.platform && stateNav?.sku) {
      const idx = PLATFORM_TAB_INDEX[stateNav.platform];
      if (idx) {
        setPlatformNav({ platform: stateNav.platform, sku: stateNav.sku });
        setTabValue(idx);
      }
      window.history.replaceState({}, document.title);
      return;
    }
    const platform = searchParams.get("platform");
    const sku = searchParams.get("sku");
    if (platform && sku) {
      const idx = PLATFORM_TAB_INDEX[platform];
      if (idx) {
        setPlatformNav({ platform, sku });
        setTabValue(idx);
      }
      setSearchParams({}, { replace: true });
    }
  }, [location.state, searchParams, setSearchParams]);

  return (
    <DashboardLayout>
      <MDBox>
        <TaskStatusWidget />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <MDTypography variant="h4" fontWeight="medium" color="dark" mb={3}>
          Multi-Marketplace Repricer
        </MDTypography>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Overview" />
            <Tab label="Base Articles" />
            <Tab label="Otto" />
            <Tab label="Kaufland" />
            <Tab label="Shopify" />
            <Tab label="FBM" />
            <Tab label="FBA" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <OverviewTab />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <BaseArticlesTab
            onNavigateToPlatform={handleNavigateToPlatform}
            initialSku={
              platformNav?.platform === "base" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <OttoTab
            initialSku={
              platformNav?.platform === "otto" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
            onNavigateToBase={handleNavigateToBase}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <KauflandTab
            initialSku={
              platformNav?.platform === "kaufland" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
            onNavigateToBase={handleNavigateToBase}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <ShopifyTab
            initialSku={
              platformNav?.platform === "shopify" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
            onNavigateToBase={handleNavigateToBase}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <FbmTab
            initialSku={
              platformNav?.platform === "fbm" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
            onNavigateToBase={handleNavigateToBase}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <FbaTab
            initialSku={
              platformNav?.platform === "fba" ? platformNav.sku : null
            }
            onNavConsumed={() => setPlatformNav(null)}
            onNavigateToBase={handleNavigateToBase}
          />
        </TabPanel>
      </MDBox>
    </DashboardLayout>
  );
};

export default MultiRepricerPage;
